import { useToast } from '@chakra-ui/react'
import React from 'react'
import { Cookies } from 'react-cookie'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import Storage from 'src/services/storage'
import ApiError from 'src/shared/ApiError'
import { signInApi } from '../../apis/signInApi'
import { useIamStore } from '../../apis/store'
import { IAM_ERRORS } from '../../errors'

type Credentials = {
  email: string
  password: string
}

type Hooks = {
  setCredentials: (credentials: Credentials) => void
  isLoading: boolean
}

const useLogIn = (): Hooks => {
  const history = useHistory()
  const cookies = React.useMemo(() => {
    return new Cookies()
  }, [])
  const { login, send } = useIamStore((store) => ({ login: store.saveAccessToken, send: store.sendEvent }))
  const toast = useToast()
  const [t] = useTranslation()
  const RawErrors: Record<string, string> = React.useMemo(
    () => ({
      [IAM_ERRORS.USER_NOT_FOUND]: t('login.validation.User not found'),
      [IAM_ERRORS.INTERNAL_ERROR]: t('login.validation.Something went wrong'),
      [IAM_ERRORS.USER_NOT_CONFIRMED]: t('login.validation.user-not-confirmed'),
      [IAM_ERRORS.INCORRECT_CREDENTIALS]: t('login.validation.incorrect-credentials'),
      [IAM_ERRORS.PASSWORD_RESET_REQUIRED]: t('login.validation.reset-password-required'),
      [IAM_ERRORS.INVALID_CODE]: t('login.validation.invalid-code')
    }),
    [t],
  )
  const [state, setState] = React.useState<{
    errors: ApiError[]
    loading: boolean
  }>({
    errors: [],
    loading: false,
  })

  const setCredentials = React.useCallback(
    (credentials: Credentials) => {
      setState((prevState) => ({ ...prevState, loading: true }))
      signInApi(credentials)
        .then((data) => {
          setState((prevState) => ({ ...prevState, loading: false }))
          login(data.accessToken)
          cookies.set('authToken', data.accessToken)
          send({ type: 'VALID_CREDENTIALS' })
          if (Storage.path.get()) {
            history.push(Storage.path.get())
            Storage.path.remove()
          }
        })
        .catch((error) => {
          setState((prevState) => ({ ...prevState, loading: false }))
          const message = RawErrors[error?.code] ?? RawErrors.INTERNAL_ERROR
          send({ type: 'INVALID_CREDENTIALS' })
          toast({
            title: message,
            isClosable: true,
            duration: 5000,
            status: 'error',
          })
        })
    },
    [login, cookies, send, history, RawErrors, toast],
  )

  return { setCredentials, isLoading: state.loading }
}

export default useLogIn
